import { API_CONFIG } from './config';
import { retryAfterMs } from './retry-after';

/**
 * Portal Auth API — recipient-facing authentication.
 *
 * Uses the same NestJS backend but separate JWT namespace (portal).
 * Calls go directly to the backend (no Next.js API proxy needed),
 * matching the existing lib/api/auth.ts pattern.
 */

const PORTAL_TOKEN_KEY = 'hf-portal:token';
const PORTAL_RECIPIENT_KEY = 'hf-portal:recipient';
const PORTAL_REFRESH_TOKEN_KEY = 'hf-portal:refresh-token';
const PORTAL_ADMIN_PREVIEW_KEY = 'hf-portal:admin-preview';
const PORTAL_SESSION_IDENTITY_KEY = 'hf-portal:session-identity';

/**
 * WHERE A PORTAL SESSION LIVES (17 Oct 2026).
 *
 * A genuine employee's session is shared by every tab of the browser
 * (localStorage): sign in once, open the course in a second tab, still signed
 * in. An ADMIN PREVIEW ("View as in Portal") must never replace it. It used to:
 * previewing Nina overwrote Omar's session on the same machine, and Exit signed
 * Omar out too. A preview session therefore lives in its own TAB
 * (sessionStorage), and this key, in that tab's sessionStorage, is what says so.
 * Every session read and write below goes through `portalStore()`.
 *
 * The flag outlives a preview's exit on purpose: an emptied preview tab reads
 * as signed out, not as whichever real employee shares the browser. A genuine
 * sign-in in the tab (`setSession` without a preview marker) is what leaves
 * preview mode.
 */
const PORTAL_TAB_SESSION_KEY = 'hf-portal:tab-session';
const PORTAL_SESSION_KEYS = [
  PORTAL_TOKEN_KEY,
  PORTAL_RECIPIENT_KEY,
  PORTAL_REFRESH_TOKEN_KEY,
  PORTAL_ADMIN_PREVIEW_KEY,
  PORTAL_SESSION_IDENTITY_KEY,
];

/**
 * How a preview reaches its new tab. A new tab starts with an empty
 * sessionStorage, so the opener leaves the session here, in shared storage, for
 * as short a time as it takes the tab to load, and the tab claims it with the
 * one-time `pv` parameter of its own address. The parameter is a claim ticket,
 * never a credential: the tokens themselves are not on the URL (#417).
 */
const PORTAL_PREVIEW_HANDOFF_KEY = 'hf-portal:preview-handoff';
const PORTAL_PREVIEW_HANDOFF_TTL_MS = 2 * 60 * 1000;
export const PORTAL_PREVIEW_CLAIM_PARAM = 'pv';

function tabIsPreview(): boolean {
  try {
    return window.sessionStorage.getItem(PORTAL_TAB_SESSION_KEY) === 'preview';
  } catch {
    return false;
  }
}

/** The claim already tried from this page's address (a failed claim is not retried on every read). */
let claimTriedFromAddress: string | null = null;

/** The storage this tab's portal session is in: its own, for a preview; the shared one otherwise. */
function portalStore(): Storage {
  claimPreviewFromAddress();
  return tabIsPreview() ? window.sessionStorage : window.localStorage;
}

/** Shared across tabs (own, for a preview), and stable across rotation within the same login. */
function portalSessionIdentity(): string | null {
  if (typeof window === 'undefined') return null;
  return portalStore().getItem(PORTAL_SESSION_IDENTITY_KEY);
}

function isCurrentPortalSession(identity: string | null): boolean {
  return portalSessionIdentity() === identity;
}

function replacePortalSessionIdentity(): void {
  const identity = Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-');
  // Invalidate pending operations before replacing any of their credentials.
  portalStore().setItem(PORTAL_SESSION_IDENTITY_KEY, identity);
}

function newPreviewClaim(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

interface PreviewHandoff {
  claim: string;
  at: number;
  session: PortalSessionInput;
}

function readPreviewHandoff(): PreviewHandoff | null {
  try {
    const raw = window.localStorage.getItem(PORTAL_PREVIEW_HANDOFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PreviewHandoff> | null;
    if (!parsed || typeof parsed.claim !== 'string' || typeof parsed.at !== 'number' || !parsed.session) return null;
    return parsed as PreviewHandoff;
  } catch {
    return null;
  }
}

function removePreviewHandoff(): void {
  try {
    window.localStorage.removeItem(PORTAL_PREVIEW_HANDOFF_KEY);
  } catch {
    // Nothing to remove with.
  }
}

/** A hand-off nobody claimed is a credential left lying in shared storage: it is removed once it is stale. */
function purgeStalePreviewHandoff(): void {
  const handoff = readPreviewHandoff();
  if (handoff && Date.now() - handoff.at > PORTAL_PREVIEW_HANDOFF_TTL_MS) removePreviewHandoff();
}

/** A return path must stay inside this site. */
function sameSitePath(value: unknown): string | null {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
    ? value
    : null;
}

/** The marker that names whose account an admin preview shows (and where Exit returns to). */
function previewMarker(recipient: PortalRecipient, returnPath?: string | null): PortalAdminPreview {
  const safeReturn = sameSitePath(returnPath ?? null);
  return {
    recipientId: recipient.id,
    recipientName: [recipient.firstName, recipient.lastName].filter(Boolean).join(' '),
    recipientEmail: recipient.email,
    ...(safeReturn ? { returnPath: safeReturn } : {}),
  };
}

/** Where Exit returns an admin to when the preview tab cannot close itself. */
export function previewReturnPath(preview: PortalAdminPreview | null | undefined): string {
  return sameSitePath(preview?.returnPath) ?? PORTAL_PREVIEW_RETURN_PATH;
}

/** A tab opened as a preview claims its session from its own address. */
function claimPreviewFromAddress(): void {
  if (typeof window === 'undefined') return;
  let claim: string | null = null;
  try {
    claim = new URLSearchParams(window.location.search).get(PORTAL_PREVIEW_CLAIM_PARAM);
  } catch {
    return;
  }
  if (!claim || claim === claimTriedFromAddress || tabIsPreview()) return;
  claimTriedFromAddress = claim;
  try {
    portalAuthApi.claimPreviewSession(claim);
  } catch {
    // Site data blocked in this tab: it cannot hold a preview, and signs in like anyone else.
  }
}

/** localStorage events reach other tabs; this event also updates this tab's
 * persistent layout after login, renewal and sign-out. */
export const PORTAL_SESSION_EVENT = 'hf-portal:session-changed';
function notifyPortalSession(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(PORTAL_SESSION_EVENT));
}

/**
 * The refresh currently in flight, and the raw token it is spending.
 *
 * SINGLE-FLIGHT, because portal rotation is now SINGLE-WINNER server-side
 * (`UPDATE … WHERE id = $1 AND revoked_at IS NULL RETURNING id`, and the
 * service refuses to issue when no row comes back). One token must not produce
 * two live 30-day chains — but the losers of a race now get a hard 401 where
 * they used to get a second chain, and the portal races on EVERY access-token
 * expiry: three independent call sites each invoke `refresh()` on their own
 * 401 (`authedFetch`, `use-portal-training.ts`, `use-portal-certificates.ts`)
 * and a page that fires training + certificates in parallel POSTs the same raw
 * token two or three times. Without this, the learner is signed out mid SCORM
 * attempt with no password to get back in.
 *
 * `lib/api/client.ts` has kept exactly this guard for the admin app since it
 * was written; the portal was simply never given one.
 */
let portalRefreshInFlight: Promise<string | null> | null = null;
let portalRefreshTokenInFlight: string | null = null;
let portalRefreshIdentityInFlight: string | null | undefined;

/**
 * Where the "Exit preview" control in the portal shell sends the admin back to.
 * The only caller of `openPortalAsRecipient` today is the recipients list.
 */
export const PORTAL_PREVIEW_RETURN_PATH = '/recipients';

export interface PortalRecipient {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  workspaceId: string;
  companyId: string;
  domainId: string | null;
  /**
   * Whether this recipient manages at least one other recipient (some other
   * `recipients` row has `manager_id = this.id`, scoped to the same workspace,
   * not soft-deleted). Computed server-side at JWT issuance.
   *
   * Used by the User Portal to decide whether to render the Manager View
   * toggle. Defaults to `false` on legacy login responses issued before this
   * field was added — the toggle stays hidden until the user re-logs.
   */
  isManager?: boolean;
  /**
   * The learner's language as one of the interface languages ('ar'), from the
   * directory, a CSV or a form; null when the portal has no catalogue for it.
   * The portal opens in it on a device where no language was chosen yet
   * (locale audit 2026-09-26, L-02). Absent from a login made before the API
   * sent it.
   */
  locale?: string | null;
}

/**
 * Why the API refused a portal one-time code — the `errorCode` it stamps on
 * the refusal (apps/api/src/auth/portal/portal-auth.service.ts,
 * PORTAL_OTP_ERROR_CODES). The login page words each one in the learner's
 * language instead of showing the API's English message
 * (lib/user-portal/sign-in-refusal.ts). Kept equal to the API's list by
 * app/(portal)/portal/login/__tests__/refusal-codes-match-the-api.guard.spec.ts.
 */
export const PORTAL_OTP_ERROR = {
  invalid: 'portal_otp_invalid',
  expired: 'portal_otp_expired',
  rateLimited: 'portal_otp_rate_limited',
  /** The code was right, but the learner can no longer sign in (removed, inactive, no portal access). */
  accountInactive: 'portal_account_inactive',
} as const;

/**
 * Why the API refused a portal magic link (PORTAL_MAGIC_LINK_ERROR_CODES in
 * apps/api/src/auth/portal/portal-auth.service.ts). A right link for a learner
 * who can no longer sign in answers PORTAL_OTP_ERROR.accountInactive. Kept
 * equal to the API's list by the same guard as PORTAL_OTP_ERROR.
 */
export const PORTAL_MAGIC_LINK_ERROR = {
  /**
   * The link cannot be used: none on file (already used, or past its 15
   * minutes), or not the one on file (a newer link replaced it, or it was
   * altered). One answer, so a guess never learns whether a link was requested.
   */
  invalid: 'portal_magic_link_invalid',
  /** The real link, but past its expiry. */
  expired: 'portal_magic_link_expired',
  rateLimited: 'portal_magic_link_rate_limited',
} as const;

/**
 * Why the API refused a portal single sign-on (PORTAL_SSO_ERROR_CODES in
 * apps/api/src/auth/portal/portal-auth.service.ts). Kept equal to the API's
 * list by the same guard as PORTAL_OTP_ERROR.
 */
export const PORTAL_SSO_ERROR = {
  notConfigured: 'portal_sso_not_configured',
  /** The sign-in attempt is unknown, used or past its 10 minutes. */
  expired: 'portal_sso_expired',
  /** The provider did not confirm who the learner is. */
  failed: 'portal_sso_failed',
  noAccount: 'portal_sso_no_account',
  /** The workspace requires MFA and the provider did not report it. */
  mfaRequired: 'portal_sso_mfa_required',
  /** The provider's answer carried no usable email address. */
  emailMissing: 'portal_sso_email_missing',
  /** A SAMLResponse (or the body posting it) over 1 MB: refused before it is decoded. */
  responseTooLarge: 'portal_sso_response_too_large',
  /** The OIDC provider names a token or key address HumanFirewall will not call (not https, another host, private). */
  endpointNotAllowed: 'portal_sso_endpoint_not_allowed',
} as const;

/**
 * The learner's organisation is switched off (Suspended, Cancelled or
 * deleted): the `errorCode` of apps/api/src/auth/company-enabled.ts, which
 * every portal sign-in answers with once the code has matched.
 */
export const COMPANY_DISABLED_ERROR_CODE = 'company_disabled';

/**
 * A refused portal-auth call: the API's message (English, for logs), the HTTP
 * status and the API's `errorCode` when it sent one. Show the learner a
 * sentence chosen from `code` / `status`, never `message`.
 */
export class PortalAuthError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    // `extends Error` + a downlevelled target can break `instanceof`.
    Object.setPrototypeOf(this, PortalAuthError.prototype);
    this.name = 'PortalAuthError';
    this.status = status;
    this.code = code;
  }
}

/**
 * The PortalAuthError for a refused portal-auth response; `fallback` when the
 * body says nothing. Both portal sign-in clients throw it — `portalAuthApi`
 * below (OTP, magic link) and `portalSsoApi` in
 * components/portal/hooks/use-portal-sso.ts (OIDC, SAML, refresh) — so a page
 * can word the refusal from its code (lib/user-portal/sign-in-refusal.ts).
 */
export async function portalAuthError(res: Response, fallback: string): Promise<PortalAuthError> {
  const body = (await res.json().catch(() => ({}))) as { message?: unknown; errorCode?: unknown };
  const message = Array.isArray(body.message)
    ? body.message.join('; ')
    : typeof body.message === 'string' && body.message
      ? body.message
      : fallback;
  const code = typeof body.errorCode === 'string' ? body.errorCode : null;
  return new PortalAuthError(message, res.status, code);
}

/**
 * Marker written alongside the portal session when an ADMIN primed it via
 * "View as in Portal" (`openPortalAsRecipient`). A recipient who logs in
 * normally never has one, so its presence is what the portal shell keys the
 * impersonation banner + exit control off (#417).
 */
export interface PortalAdminPreview {
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  /**
   * The admin page the preview was started from (a path on this site). Exit
   * returns there when the tab cannot close itself; absent on a marker written
   * before this existed, which falls back to PORTAL_PREVIEW_RETURN_PATH.
   */
  returnPath?: string;
}

export interface PortalLoginResponse {
  accessToken: string;
  recipient: PortalRecipient;
}

export interface PortalSessionInput extends PortalLoginResponse {
  refreshToken?: string | null;
  adminPreview?: PortalAdminPreview | null;
}

export interface PortalWorkspaceInfo {
  workspaceId: string;
  workspaceName: string;
  companyName: string;
  domainName: string | null;
}

function portalUrl(path: string): string {
  return `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/portal/auth${path}`;
}

export const portalAuthApi = {
  /**
   * POST /v1/portal/auth/send-otp
   * Sends a 6-digit OTP to the recipient's email.
   * Always returns 200 (anti-enumeration).
   *
   * `workspaceId`, as on sendMagicLink: the code is then for that workspace.
   */
  async sendOtp(email: string, workspaceId?: string): Promise<{ message: string }> {
    const res = await fetch(portalUrl('/send-otp'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, workspaceId }),
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Failed to send OTP');

    return res.json();
  },

  /**
   * POST /v1/portal/auth/verify-otp
   * Verifies the OTP and returns a portal JWT + recipient info.
   *
   * `workspaceId`: the API refuses a code issued for another workspace.
   */
  async verifyOtp(email: string, otp: string, workspaceId?: string): Promise<PortalLoginResponse> {
    const res = await fetch(portalUrl('/verify-otp'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp, workspaceId }),
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Invalid or expired OTP');

    return res.json();
  },

  /**
   * POST /v1/portal/auth/send-magic-link
   * Sends a magic link to the recipient's email.
   *
   * `workspaceId` is optional but STRONGLY RECOMMENDED — when supplied,
   * the BE scopes the lookup + Redis key by (workspaceId, email) instead
   * of email-only. This closes the MSP-scenario ambiguity where the
   * same email belongs to multiple workspaces and the BE would otherwise
   * silently pick one. The verify endpoint also matches the same scoped
   * Redis key shape via `?ws=` in the magic-link URL. Added 2026-05-14.
   */
  async sendMagicLink(
    email: string,
    workspaceId?: string,
  ): Promise<{ message: string }> {
    const res = await fetch(portalUrl('/send-magic-link'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, workspaceId }),
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Failed to send magic link');

    return res.json();
  },

  /**
   * GET /v1/portal/auth/verify?token=<token>&email=<email>&ws=<workspaceId>
   * Verify a magic link token and return a portal JWT + recipient info.
   *
   * The `ws` param is forwarded from the URL the recipient clicked (the BE
   * embeds it into the email's magic-link URL when the workspace was known
   * at send time). When present, the BE uses the workspace-scoped Redis
   * key. When absent (Safe Links stripped the query), the BE finds the
   * workspace from the token itself, then falls back to the legacy
   * email-only key — so `ws` is never needed to redeem (audit P6).
   */
  async verifyMagicLink(
    token: string,
    email: string,
    workspaceId?: string,
  ): Promise<PortalLoginResponse> {
    const qs = new URLSearchParams({ token, email });
    if (workspaceId) qs.set('ws', workspaceId);
    const res = await fetch(portalUrl(`/verify?${qs.toString()}`), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Invalid or expired magic link');

    return res.json();
  },

  // ── Token helpers ─────────────────────────────────────────

  /** Replace the whole login, including credentials absent from its response.
   * OTP/magic links have no refresh token and must not inherit one from SSO. */
  setSession(session: PortalSessionInput): void {
    if (typeof window === 'undefined') return;
    // WHICH STORE. A preview session lives in this tab alone; a genuine
    // sign-in is shared by the browser. A genuine sign-in in a tab that was a
    // preview therefore ENDS the preview here: its leftovers are dropped and the
    // session goes to the shared store like any other. Decided before anything
    // is written, because every write below follows it.
    if (session.adminPreview) {
      window.sessionStorage.setItem(PORTAL_TAB_SESSION_KEY, 'preview');
    } else if (tabIsPreview()) {
      for (const key of PORTAL_SESSION_KEYS) window.sessionStorage.removeItem(key);
      window.sessionStorage.removeItem(PORTAL_TAB_SESSION_KEY);
    }
    const store = portalStore();
    replacePortalSessionIdentity();
    portalRefreshInFlight = null;
    portalRefreshTokenInFlight = null;
    portalRefreshIdentityInFlight = undefined;
    // ORDER MATTERS. Another portal tab hears these writes one storage event
    // per key and reads storage as it stands at each (useLearnerLanguage,
    // AdminPreviewBanner). A recipient beside the wrong "View as" marker reads
    // as that person signed in, and the language hook then saves an employee's
    // language as the admin's own. Every order that swaps the marker and the
    // recipient key-by-key passes through such a moment (one view-as to
    // another always does), so the old recipient goes FIRST: in between, that
    // tab sees no one signed in, then the new session whole.
    store.removeItem(PORTAL_RECIPIENT_KEY);
    if (session.adminPreview) store.setItem(PORTAL_ADMIN_PREVIEW_KEY, JSON.stringify(session.adminPreview));
    else store.removeItem(PORTAL_ADMIN_PREVIEW_KEY);
    store.setItem(PORTAL_TOKEN_KEY, session.accessToken);
    store.setItem(PORTAL_RECIPIENT_KEY, JSON.stringify(session.recipient));
    if (session.refreshToken) store.setItem(PORTAL_REFRESH_TOKEN_KEY, session.refreshToken);
    else store.removeItem(PORTAL_REFRESH_TOKEN_KEY);
    notifyPortalSession();
  },

  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return portalStore().getItem(PORTAL_TOKEN_KEY);
  },

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    portalStore().setItem(PORTAL_TOKEN_KEY, token);
    notifyPortalSession();
  },

  getRecipient(): PortalRecipient | null {
    if (typeof window === 'undefined') return null;
    const raw = portalStore().getItem(PORTAL_RECIPIENT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PortalRecipient;
    } catch {
      return null;
    }
  },

  setRecipient(recipient: PortalRecipient): void {
    if (typeof window === 'undefined') return;
    portalStore().setItem(PORTAL_RECIPIENT_KEY, JSON.stringify(recipient));
    notifyPortalSession();
  },

  /**
   * Open the employee portal AS a recipient, in a NEW TAB, for an admin.
   *
   * The session (the admin `direct-login` token pair) must reach that tab and
   * must NOT touch this one: the admin's browser may hold a real employee's
   * session (the admin who also takes the training), and a preview used to
   * overwrite it. So the pair is left in shared storage for as long as the tab
   * takes to load, under a one-time claim ticket that rides the new tab's
   * address (`?pv=`), and the tab moves it into its own sessionStorage
   * (`claimPreviewSession`, run by `portalStore()` on the tab's first read).
   * Nothing but the ticket is on the URL (#417: a bearer token in a query
   * string leaks into history, Referer headers and proxy logs).
   *
   * The session is flagged as an ADMIN PREVIEW so the portal shell can say whose
   * account is on screen and offer a way out (#417) - without it the portal
   * renders as an indistinguishable genuine session.
   *
   * `window.open` is called WITHOUT the `noopener` feature: with it the call
   * always returns null, which this method's callers read as "pop-up blocked",
   * a toast shown over a tab that had opened. The opener link is cut straight
   * away instead (`tab.opener = null`).
   *
   * @returns the opened window handle, or null if the pop-up blocker fired (in
   *          which case nothing is left in storage).
   */
  openPortalAsRecipient(params: {
    accessToken: string;
    refreshToken?: string | null;
    recipient: PortalRecipient;
    /** The admin page to return to when Exit cannot close the tab (default: the recipients list). */
    returnPath?: string;
  }): Window | null {
    if (typeof window === 'undefined') return null;
    purgeStalePreviewHandoff();
    const claim = newPreviewClaim();
    const { returnPath, ...session } = params;
    try {
      window.localStorage.setItem(
        PORTAL_PREVIEW_HANDOFF_KEY,
        JSON.stringify({
          claim,
          at: Date.now(),
          session: { ...session, adminPreview: previewMarker(params.recipient, returnPath) },
        } satisfies PreviewHandoff),
      );
    } catch {
      // Site data blocked: the tab could not receive the session.
      return null;
    }

    const ws = encodeURIComponent(params.recipient.workspaceId);
    const tab = window.open(`/portal?ws=${ws}&${PORTAL_PREVIEW_CLAIM_PARAM}=${claim}`, '_blank');
    if (!tab) {
      removePreviewHandoff();
      return null;
    }
    try {
      tab.opener = null;
    } catch {
      // A browser that will not let the link be cut leaves it as it was.
    }
    // A ticket nobody claimed is a credential lying in shared storage.
    window.setTimeout(() => {
      if (readPreviewHandoff()?.claim === claim) removePreviewHandoff();
    }, PORTAL_PREVIEW_HANDOFF_TTL_MS);
    return tab;
  },

  /**
   * The new tab's half of `openPortalAsRecipient`: spend the hand-off for
   * `claim` and make this tab the preview. False (and nothing changes) for a
   * ticket that is not on file, is not this tab's, or went stale; a stale one is
   * removed. A matching ticket is spent whether or not it was fresh.
   */
  claimPreviewSession(claim: string): boolean {
    if (typeof window === 'undefined' || !claim) return false;
    const handoff = readPreviewHandoff();
    if (!handoff) return false;
    if (Date.now() - handoff.at > PORTAL_PREVIEW_HANDOFF_TTL_MS) {
      removePreviewHandoff();
      return false;
    }
    if (handoff.claim !== claim) return false;
    removePreviewHandoff();
    this.setSession(handoff.session);
    return true;
  },

  /**
   * Make THIS tab the preview of `recipient`: the old `/user-portal/<id>` links,
   * which do the direct login in the tab the admin clicked into and then replace
   * themselves with the real portal. Same per-tab session as the new-tab way.
   */
  startPreviewSession(params: {
    accessToken: string;
    refreshToken?: string | null;
    recipient: PortalRecipient;
    returnPath?: string;
  }): void {
    if (typeof window === 'undefined') return;
    const { returnPath, ...session } = params;
    this.setSession({ ...session, adminPreview: previewMarker(params.recipient, returnPath) });
  },

  // ── Admin preview marker (#417) ──────────────────────────────────

  getAdminPreview(): PortalAdminPreview | null {
    if (typeof window === 'undefined') return null;
    const raw = portalStore().getItem(PORTAL_ADMIN_PREVIEW_KEY);
    if (!raw) return null;
    let preview: PortalAdminPreview;
    try {
      preview = JSON.parse(raw) as PortalAdminPreview;
    } catch {
      return null;
    }
    // A marker left behind (a shared one, in a browser that held a preview
    // before previews had their own tab) must only be honoured while it still
    // describes the session actually stored - otherwise a later genuine login
    // on the same machine would be captioned with the previewed employee's name.
    return preview.recipientId === this.getRecipient()?.id ? preview : null;
  },

  setAdminPreview(preview: PortalAdminPreview): void {
    if (typeof window === 'undefined') return;
    portalStore().setItem(PORTAL_ADMIN_PREVIEW_KEY, JSON.stringify(preview));
    notifyPortalSession();
  },

  isAdminPreview(): boolean {
    return this.getAdminPreview() !== null;
  },

  // ── Refresh token (Phase 5) ──────────────────────────────────────

  /**
   * Phase 5 — raw refresh token returned alongside the access token on
   * SSO / SAML / direct-login. Stored in localStorage (same surface as
   * the access token) for now. When the portal moves behind a secure
   * cookie domain, swap this for `document.cookie` with `HttpOnly` +
   * `Secure` via a small BE endpoint; the hook surface stays the same.
   */
  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return portalStore().getItem(PORTAL_REFRESH_TOKEN_KEY);
  },

  setRefreshToken(token: string | null): void {
    if (typeof window === 'undefined') return;
    if (!token) {
      portalStore().removeItem(PORTAL_REFRESH_TOKEN_KEY);
      notifyPortalSession();
      return;
    }
    portalStore().setItem(PORTAL_REFRESH_TOKEN_KEY, token);
    notifyPortalSession();
  },

  /**
   * Call `/v1/portal/auth/refresh` with the stored refresh token and
   * persist the rotated pair. Returns the new access token on success
   * or `null` on any failure (caller falls back to login redirect).
   *
   * SINGLE-FLIGHT — see `portalRefreshInFlight` above. Concurrent callers
   * spending the SAME raw token share one request and one answer, so only one
   * of them reaches the server's single-winner rotation.
   *
   * CROSS-TAB RECOVERY. In-process de-duplication cannot cover two open portal
   * tabs: they are separate processes sharing one localStorage. So when the
   * call fails, this re-reads storage before answering — if the stored refresh
   * token is no longer the one we spent, another tab won the rotation and its
   * access token is live. Returning that beats returning `null`, which sends a
   * learner with a perfectly good session to the login screen.
   *
   * A 429 IS NOT A REFUSED SESSION (2026-11-03). The API counts per client, the
   * refresh included, and a 429 used to fall straight to the recovery below and
   * send the learner to the login screen. The throttler answers before the
   * handler runs, so nothing was rotated: the flight waits what the server says
   * (lib/api/retry-after.ts, the wait every 429 gets) and sends the same POST
   * again ONCE. Not when another tab rotated past this token meanwhile: sending
   * a spent token again is a replay, which the API answers by revoking the whole
   * chain once its grace has passed, and the recovery already has that tab's
   * answer. A second 429 goes on exactly as any other refusal does.
   */
  async refresh(identity = portalSessionIdentity()): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken || !isCurrentPortalSession(identity)) return null;

    // Join the flight already spending this exact token. A flight spending a
    // DIFFERENT token is stale and must not be joined — the caller's token is
    // the newer one.
    if (portalRefreshInFlight && portalRefreshTokenInFlight === refreshToken) {
      if (portalRefreshIdentityInFlight === identity) return portalRefreshInFlight;
    }

    portalRefreshTokenInFlight = refreshToken;
    portalRefreshIdentityInFlight = identity;
    portalRefreshInFlight = (async (): Promise<string | null> => {
      try {
        const post = () =>
          fetch(portalUrl('/refresh'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
        let res = await post();
        // 429 (see the note above): wait, then the same POST once more, unless another tab has
        // rotated past this token by then. A second 429 goes on like any other refusal.
        if (res.status === 429 && this.getRefreshToken() === refreshToken) {
          await new Promise((resolve) => setTimeout(resolve, retryAfterMs(res.headers)));
          if (!isCurrentPortalSession(identity)) return null;
          if (this.getRefreshToken() === refreshToken) res = await post();
        }
        if (!isCurrentPortalSession(identity)) return null;
        if (!res.ok) return this.recoverRotationLoser(refreshToken, identity);

        const body = (await res.json()) as {
          accessToken?: string;
          refreshToken?: string;
        };
        if (!isCurrentPortalSession(identity)) return null;
        if (!body.accessToken || !body.refreshToken) {
          return this.recoverRotationLoser(refreshToken, identity);
        }

        this.setToken(body.accessToken);
        this.setRefreshToken(body.refreshToken);
        return body.accessToken;
      } catch {
        if (!isCurrentPortalSession(identity)) return null;
        return this.recoverRotationLoser(refreshToken, identity);
      } finally {
        if (portalRefreshIdentityInFlight === identity && portalRefreshTokenInFlight === refreshToken) {
          portalRefreshInFlight = null;
          portalRefreshTokenInFlight = null;
          portalRefreshIdentityInFlight = undefined;
        }
      }
    })();

    return portalRefreshInFlight;
  },

  /**
   * A refresh failed. Did another tab already rotate past us?
   *
   * Only answers with a token when storage has genuinely MOVED ON from the one
   * we spent — otherwise it returns null and the caller redirects to login, so
   * a real expiry is still a real expiry. It never writes: the winner's tokens
   * are already in storage and clobbering them is what this exists to avoid.
   */
  recoverRotationLoser(spentRefreshToken: string, identity = portalSessionIdentity()): string | null {
    if (!isCurrentPortalSession(identity)) return null;
    const current = this.getRefreshToken();
    if (!current || current === spentRefreshToken) return null;
    const token = this.getToken();
    return isCurrentPortalSession(identity) ? token : null;
  },

  /**
   * POST /v1/portal/auth/logout — end the session server-side, then drop the
   * local tokens.
   *
   * Until this existed the portal had no logout at all: `clearSession()` cleared
   * localStorage and the 30-day refresh chain kept running on the server, so
   * signing out on a shared machine revoked nothing.
   *
   * ALWAYS clears locally, even when the network call fails — a learner who
   * pressed Sign out must not be left holding tokens because the request timed
   * out. The server-side chain revocation is what the call buys; losing it falls
   * back to today's behaviour rather than to a broken button.
   *
   * THE ACCESS TOKEN DIES TOO, since 2026-09-21. It carries a `sid` (its
   * `portal_sessions` row) that the portal JWT strategy re-reads on every
   * request, and this call revokes that row — so a copy already taken off this
   * machine stops working instead of surviving its remaining 8 hours. That is
   * why the call matters even when it looks like a formality.
   *
   * THAT NOW INCLUDES MAGIC-LINK AND OTP LOGINS — the common case. They still
   * issue NO refresh token (only SSO / SAML and admin direct-login do), so the
   * response carries `hadRefreshToken: false`; the session row is what gets
   * revoked, reported as `revokedSession: true`. The ONE residual is a token
   * minted before the claim existed: it has no sid, nothing to revoke, and lives
   * out its 8h — and the API refuses such tokens outright after 2026-09-24.
   */
  async logout(): Promise<void> {
    const identity = portalSessionIdentity();
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();
    try {
      if (accessToken || refreshToken) {
        // A dormant page may still hold a valid refresh chain after its access
        // bearer expires. Renew on 401 before revoking, while both credentials
        // remain available. The old refresh token in the body still identifies
        // the chain, including the successor issued by that renewal.
        await this.authedFetch(portalUrl('/logout'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          // Omitted, not empty: a magic-link / OTP session HAS no refresh
          // token, and an empty string is a client bug the API rejects.
          body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        }, identity);
      }
    } catch {
      // Swallowed on purpose — see the doc comment.
    } finally {
      // A newer login owns storage now; finishing this older logout must not
      // erase it. Ordinary failures still clear the session that initiated it.
      if (isCurrentPortalSession(identity)) this.clearSession();
    }
  },

  /**
   * Forget this tab's portal session. A preview tab forgets ITS session and
   * nothing else: the shared (localStorage) session of a real employee on the
   * same machine is not this tab's to clear, and the tab stays in preview mode
   * so it reads as signed out rather than as that employee.
   */
  clearSession(): void {
    if (typeof window === 'undefined') return;
    const store = portalStore();
    replacePortalSessionIdentity();
    portalRefreshInFlight = null;
    portalRefreshTokenInFlight = null;
    portalRefreshIdentityInFlight = undefined;
    store.removeItem(PORTAL_TOKEN_KEY);
    store.removeItem(PORTAL_RECIPIENT_KEY);
    store.removeItem(PORTAL_REFRESH_TOKEN_KEY);
    store.removeItem(PORTAL_ADMIN_PREVIEW_KEY);
    notifyPortalSession();
  },

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    // 2026-05-22 — also reject EXPIRED tokens, not just missing ones.
    // Pre-fix, an expired-but-present token returned `true`, the page
    // proceeded to fetch authenticated endpoints, and the user saw an
    // ugly "Course Not Available" error instead of being routed to
    // login. Decode the JWT payload (no signature check — that's the
    // BE's job) and compare `exp` (seconds since epoch) against now.
    // On any decode failure, fall through to the truthy-token answer so
    // a malformed token still triggers the login redirect via the
    // downstream `getToken()` consumer instead of silently locking the
    // user out.
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(
        // base64url → base64 → JSON
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      ) as { exp?: number };
      if (typeof payload.exp !== 'number') return true;
      // Add a 10s clock-skew buffer — treat tokens about to expire as
      // already expired so an in-flight request doesn't race the boundary.
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp > nowSec + 10;
    } catch {
      return true;
    }
  },

  /**
   * Authenticated fetch helper with automatic 401 → refresh → retry.
   * Use this wherever portal hooks hit authenticated endpoints.
   */
  async authedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    identity = portalSessionIdentity(),
  ): Promise<Response> {
    const headers = new Headers(init?.headers);
    const token = this.getToken();
    if (!isCurrentPortalSession(identity)) throw new DOMException('Portal session changed', 'AbortError');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const res = await fetch(input, { ...init, headers });
    if (res.status !== 401 || !isCurrentPortalSession(identity)) return res;

    const refreshed = await this.refresh(identity);
    if (!refreshed || !isCurrentPortalSession(identity)) return res;

    const retryHeaders = new Headers(init?.headers);
    retryHeaders.set('Authorization', `Bearer ${refreshed}`);
    return fetch(input, { ...init, headers: retryHeaders });
  },
};

// A tab opened as an admin preview claims its session the moment this module
// loads - before any component of the page has read the session, so none of
// them sees "signed out" for a frame - and any hand-off nobody claimed is
// cleared from shared storage.
if (typeof window !== 'undefined') {
  purgeStalePreviewHandoff();
  claimPreviewFromAddress();
}
